import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  BarChart3, ArrowRight, UserPlus, Key, Download, Wifi,
  Monitor, CheckCircle, ArrowDown, Shield, Clock, Zap, Sparkles, Menu, X,
} from 'lucide-react';
import { fetchPublicPlans, findPlan, formatApiKeysLabel } from '../../lib/planCatalog';

export default function HowItWorksPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    fetchPublicPlans()
      .then(setPlans)
      .catch(() => {});
  }, []);

  const freePlan = findPlan(plans, 'free');
  const proPlan = findPlan(plans, 'pro');
  const apiKeyDetail = freePlan && proPlan
    ? `Each API key is unique and tied to your account. You can create ${formatApiKeysLabel(freePlan).toLowerCase()} on the free plan (${formatApiKeysLabel(proPlan).toLowerCase()} on Pro). Revoke any key instantly if needed.`
    : 'Each API key is unique and tied to your account. Your available key limit comes from your active plan, and you can revoke any key instantly if needed.';

  const steps = [
    {
      number: '01',
      icon: UserPlus,
      title: 'Create Your Free Account',
      desc: 'Sign up with email in 30 seconds. No credit card, no broker credentials needed. Your account is ready immediately after email verification.',
      detail: 'We never ask for your broker login. TrackMyFxEA works by receiving data FROM your terminal - we never connect TO your broker.',
    },
    {
      number: '02',
      icon: Key,
      title: 'Generate Your API Key',
      desc: 'Go to Settings -> API Keys -> Create New Key. Give it a name like "My Coinexx Account". Copy the key - you\'ll paste it into the EA.',
      detail: apiKeyDetail,
    },
    {
      number: '03',
      icon: Download,
      title: 'Download & Attach the EA',
      desc: 'Download TrackMyFxEA.mq4 (or .mq5). Copy it to your MT4/MT5 Experts folder. Attach it to any chart - the symbol doesn\'t matter.',
      detail: 'The EA is completely silent - no trades, no interference with your other EAs. It just reads account data and sends it to your dashboard.',
    },
    {
      number: '04',
      icon: Wifi,
      title: 'Configure Cloud Upload',
      desc: 'In the EA parameters, set EnableCloudUpload = true and paste your API key into the CloudApiKey field. Also add our URL to MT4/MT5 WebRequest settings.',
      detail: 'Tools -> Options -> Expert Advisors -> Allow WebRequest -> Add: https://api.trackmyfxea.com. This is a one-time setup per terminal.',
    },
    {
      number: '05',
      icon: Monitor,
      title: 'Watch Your Dashboard Light Up',
      desc: 'Within 60 seconds, your dashboard shows live account data, open trades, EA performance, equity curves, and drawdown tracking. All automatic from here.',
      detail: 'The EA uploads data every 60 seconds (configurable). Your dashboard updates in real-time. Add more accounts by attaching the EA to other terminals with the same API key.',
    },
  ];

  return (
    <div className="min-h-screen bg-[#070912] text-gray-100 overflow-x-hidden relative">
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] rounded-full bg-cyan-500/8 blur-[140px] animate-float-slow" />
        <div className="absolute top-1/3 right-0 w-[500px] h-[500px] rounded-full bg-emerald-500/6 blur-[130px] animate-float" />
        <div className="absolute bottom-0 left-1/3 w-[550px] h-[550px] rounded-full bg-purple-500/6 blur-[140px] animate-float-slow" />
      </div>

      <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${scrolled ? 'bg-[#070912]/85 backdrop-blur-xl border-b border-white/8' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 group">
            <div className="w-9 h-9 bg-gradient-to-br from-emerald-400 via-teal-400 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-500/30 group-hover:scale-105 transition-transform">
              <BarChart3 size={18} className="text-white" />
            </div>
            <span className="text-lg font-bold text-white font-display">TrackMyFxEA</span>
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <Link to="/explore" className="text-sm text-gray-400 hover:text-white transition-colors">Explore</Link>
            <Link to="/features" className="text-sm text-gray-400 hover:text-white transition-colors">Features</Link>
            <Link to="/how-it-works" className="text-sm text-emerald-400 font-medium">How It Works</Link>
            <Link to="/pricing" className="text-sm text-gray-400 hover:text-white transition-colors">Pricing</Link>
            <Link to="/ea-store" className="text-sm text-gray-400 hover:text-white transition-colors">EA Store</Link>
          </div>
          <div className="hidden md:flex items-center gap-3">
            <Link to="/login" className="text-sm text-gray-300 hover:text-white transition-colors px-4 py-2">Sign In</Link>
            <Link to="/register" className="text-sm bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-semibold px-5 py-2.5 rounded-xl hover:shadow-lg hover:shadow-emerald-500/30 transition-all">Get Started</Link>
          </div>
          <button type="button" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden text-gray-300 hover:text-white p-2" aria-label="Toggle navigation">
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-white/8 bg-[#070912]/95 backdrop-blur-xl px-4 py-4 space-y-3">
            <Link to="/explore" className="block text-sm text-gray-400 hover:text-white py-2" onClick={() => setMobileMenuOpen(false)}>Explore</Link>
            <Link to="/features" className="block text-sm text-gray-400 hover:text-white py-2" onClick={() => setMobileMenuOpen(false)}>Features</Link>
            <Link to="/how-it-works" className="block text-sm text-emerald-400 py-2" onClick={() => setMobileMenuOpen(false)}>How It Works</Link>
            <Link to="/pricing" className="block text-sm text-gray-400 hover:text-white py-2" onClick={() => setMobileMenuOpen(false)}>Pricing</Link>
            <Link to="/ea-store" className="block text-sm text-gray-400 hover:text-white py-2" onClick={() => setMobileMenuOpen(false)}>EA Store</Link>
            <hr className="border-white/8" />
            <Link to="/login" className="block text-sm text-gray-300 hover:text-white py-2" onClick={() => setMobileMenuOpen(false)}>Sign In</Link>
            <Link to="/register" className="block text-sm bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-medium px-5 py-2.5 rounded-xl text-center" onClick={() => setMobileMenuOpen(false)}>Get Started</Link>
          </div>
        )}
      </nav>

      <section className="pt-32 md:pt-40 pb-16 px-4 md:px-6 text-center relative">
        <div className="max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-4 py-1.5 mb-7 backdrop-blur-sm animate-fade-in-down">
            <Sparkles size={12} className="text-cyan-400" />
            <span className="text-xs text-gray-300">Setup in under 2 minutes</span>
          </div>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-5 font-display animate-fade-in-up">
            Live in <span className="bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400 bg-clip-text text-transparent">5 Simple Steps</span>
          </h1>
          <p className="text-gray-400 max-w-xl mx-auto text-base md:text-lg animate-fade-in-up">
            From zero to full EA analytics in under 2 minutes. No coding, no complex setup.
          </p>
        </div>
      </section>

      <section className="px-4 md:px-6 pb-20 relative">
        <div className="max-w-3xl mx-auto space-y-2">
          {steps.map((step, index) => (
            <React.Fragment key={step.number}>
              <div
                className="group relative p-6 md:p-8 rounded-2xl border transition-all duration-300 hover:-translate-y-0.5 backdrop-blur-sm overflow-hidden"
                style={{ background: 'rgba(255,255,255,0.02)', borderColor: 'rgba(255,255,255,0.08)' }}
                onMouseEnter={(event) => {
                  event.currentTarget.style.borderColor = 'rgba(16,185,129,0.3)';
                  event.currentTarget.style.boxShadow = '0 8px 32px rgba(0,0,0,0.3), 0 0 30px rgba(16,185,129,0.1)';
                }}
                onMouseLeave={(event) => {
                  event.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)';
                  event.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div className="absolute -top-12 -right-12 w-32 h-32 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-3xl pointer-events-none bg-emerald-500/15" />
                <div className="flex items-start gap-5 md:gap-6 relative">
                  <div className="flex-shrink-0">
                    <div
                      className="w-14 h-14 md:w-16 md:h-16 rounded-2xl flex items-center justify-center transition-transform group-hover:scale-110"
                      style={{
                        background: 'linear-gradient(135deg, rgba(16,185,129,0.15), rgba(20,184,166,0.05))',
                        border: '1px solid rgba(16,185,129,0.25)',
                        boxShadow: '0 0 20px rgba(16,185,129,0.1)',
                      }}
                    >
                      <step.icon size={26} className="text-emerald-400" />
                    </div>
                    <div className="text-center mt-2">
                      <span className="text-[10px] font-mono font-bold text-emerald-500/60 tracking-widest">{step.number}</span>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-xl md:text-2xl font-bold text-white mb-2 font-display">{step.title}</h3>
                    <p className="text-gray-400 mb-4 leading-relaxed text-sm md:text-base">{step.desc}</p>
                    <p className="text-sm text-gray-500 border-l-2 border-emerald-500/30 pl-4 leading-relaxed">{step.detail}</p>
                  </div>
                </div>
              </div>
              {index < steps.length - 1 && (
                <div className="flex justify-center py-2">
                  <ArrowDown size={18} className="text-emerald-500/40 animate-bounce-soft" />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </section>

      <section className="px-4 md:px-6 py-16 bg-[#080c14] relative">
        <div className="max-w-3xl mx-auto">
          <div
            className="rounded-2xl p-6 md:p-8 backdrop-blur-sm"
            style={{
              background: 'linear-gradient(135deg, rgba(16,185,129,0.08), rgba(16,185,129,0.02))',
              border: '1px solid rgba(16,185,129,0.25)',
              boxShadow: '0 0 40px rgba(16,185,129,0.08)',
            }}
          >
            <div className="flex items-start gap-4">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: 'rgba(16,185,129,0.15)', boxShadow: '0 0 20px rgba(16,185,129,0.2)' }}
              >
                <Shield size={22} className="text-emerald-400" />
              </div>
              <div>
                <h3 className="text-white font-bold mb-3 text-lg font-display">Your Security Matters</h3>
                <ul className="space-y-2.5 text-sm text-gray-300">
                  <li className="flex items-start gap-2.5"><CheckCircle size={14} className="text-emerald-400 mt-0.5 flex-shrink-0" /> We never access your broker account - the EA only sends data out</li>
                  <li className="flex items-start gap-2.5"><CheckCircle size={14} className="text-emerald-400 mt-0.5 flex-shrink-0" /> No broker credentials stored - only trade data (tickets, P&L, symbols)</li>
                  <li className="flex items-start gap-2.5"><CheckCircle size={14} className="text-emerald-400 mt-0.5 flex-shrink-0" /> API keys can be revoked instantly from your dashboard</li>
                  <li className="flex items-start gap-2.5"><CheckCircle size={14} className="text-emerald-400 mt-0.5 flex-shrink-0" /> All data encrypted in transit (HTTPS/TLS)</li>
                  <li className="flex items-start gap-2.5"><CheckCircle size={14} className="text-emerald-400 mt-0.5 flex-shrink-0" /> The EA is open-source - inspect the code yourself</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="px-4 md:px-6 py-16 md:py-20 relative">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-10 font-display">Requirements</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {[
              { icon: Monitor, title: 'MT4 or MT5', desc: 'Any broker, any version. Windows VPS or local PC.' },
              { icon: Clock, title: 'Always Running', desc: 'EA needs to be attached to a chart 24/5 for continuous sync.' },
              { icon: Zap, title: 'Internet Access', desc: 'Terminal needs internet to upload data (standard for any EA).' },
            ].map(({ icon: Icon, title, desc }) => (
              <div
                key={title}
                className="text-center p-6 rounded-2xl backdrop-blur-sm transition-all hover:-translate-y-1"
                style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.08)' }}
              >
                <div
                  className="w-14 h-14 mx-auto mb-4 rounded-2xl flex items-center justify-center"
                  style={{ background: 'rgba(34,211,238,0.1)', boxShadow: '0 0 20px rgba(34,211,238,0.15)' }}
                >
                  <Icon size={26} className="text-cyan-400" />
                </div>
                <h3 className="text-white font-semibold mb-2 text-base">{title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 md:px-6 py-20 md:py-24 text-center relative">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-5 font-display">
            Ready to <span className="bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">Get Started?</span>
          </h2>
          <p className="text-gray-400 mb-9 text-base md:text-lg">Create your free account and have your dashboard running in under 2 minutes.</p>
          <Link to="/register" className="inline-flex items-center gap-2.5 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold px-8 py-4 rounded-xl text-base md:text-lg hover:shadow-xl hover:shadow-emerald-500/40 hover:-translate-y-0.5 transition-all">
            Create Free Account <ArrowRight size={20} />
          </Link>
        </div>
      </section>

      <footer className="border-t border-white/8 px-6 py-10 text-center text-xs text-gray-600 bg-[#060810]">
        <p>Copyright 2026 TrackMyFxEA. All rights reserved.</p>
        <p className="mt-2"><a href="mailto:support@trackmyfxea.com" className="hover:text-gray-400 transition-colors">support@trackmyfxea.com</a></p>
<p className="mt-3"><Link to="/privacy" className="mx-2 hover:text-gray-400 transition-colors">Privacy</Link><Link to="/terms" className="mx-2 hover:text-gray-400 transition-colors">Terms</Link><Link to="/risk-disclaimer" className="mx-2 hover:text-gray-400 transition-colors">Risk Disclaimer</Link></p>
      </footer>
    </div>
  );
}
